import { Route, Navigate, Outlet } from 'react-router-dom';
import { ProtectedRoute } from 'ProtectedRoute';
import { type RouteRegistryEntry } from 'FrontRoyalRouteRegistry/FrontRoyalRouteRegistry.types';
import { AdminUsersComponent } from './components/AdminUsersComponent';
import { BatchEditUsers } from './components/BatchEditUsers';
import { AdminMba } from './components/AdminMba';
import { AdminGlobalMetadata } from './components/AdminGlobalMetadata';

/*
    WARNING: anything imported into this file will end up in the main javascript bundle. Our
    intention has always been to keep admin code out of the main bundle and lazy-load it
    via loadAdminDependencies.

    As long as our admin code is all still in angular, we are still following that vision, because
    we're not importing the angular module here. Instead, we're ensuring that the angular code is
    loaded via useLoadAdminDependencies before trying to render anything.

    But, once we want to move top-level admin components over to react, we're going to have
    to have a different way to enforce the lazy-loading of the code.
*/

const USERS_BASE = '/admin/users';

// Define all admin route paths
export const ADMIN_ROUTES = {
    USERS: {
        path: USERS_BASE,
        APPLICANTS: { path: `${USERS_BASE}/applicants` },
        ALL: { path: `${USERS_BASE}/all` },
    },
    BATCH_USERS: { path: '/admin/batch-users/:section?' },
    MBA: { path: '/admin/mba/:section?' },
    GROUPS_REDIRECT: { path: '/admin/groups' },
    GLOBAL_METADATA: { path: '/admin/global-metadata' },
};

// Wrapper component that applies the 'admin' class to app-root
function AdminRoutesWrapper() {
    return (
        <div className="admin">
            <Outlet />
        </div>
    );
}

export function configureAdminRoutes(): RouteRegistryEntry[] {
    return [
        {
            routes: ADMIN_ROUTES,
            config: {
                errorHandlingRules: 'admin',
            },
        },
    ];
}

export function getAdminRoutes() {
    return (
        <Route id="admin-routes" element={<AdminRoutesWrapper />}>
            {/* Admin users routes */}
            <Route
                path={ADMIN_ROUTES.USERS.APPLICANTS.path}
                element={<Navigate to={ADMIN_ROUTES.USERS.path} replace />}
            />
            <Route path={ADMIN_ROUTES.USERS.ALL.path} element={<Navigate to={ADMIN_ROUTES.USERS.path} replace />} />
            <Route
                path={ADMIN_ROUTES.USERS.path}
                element={
                    <ProtectedRoute accessPolicy="hasSuperEditorOrInterviewerAccess">
                        <AdminUsersComponent />
                    </ProtectedRoute>
                }
            />

            {/* Batch edit users routes */}
            <Route path="/admin/batch-users" element={<Navigate to="/admin/batch-users/select-users" replace />} />
            <Route
                path={ADMIN_ROUTES.BATCH_USERS.path}
                element={
                    <ProtectedRoute accessPolicy="hasAdminAccess">
                        <BatchEditUsers />
                    </ProtectedRoute>
                }
            />

            {/* Admin MBA routes */}
            <Route path="/admin/mba" element={<Navigate to="/admin/mba/calendar" replace />} />
            <Route path={ADMIN_ROUTES.GROUPS_REDIRECT.path} element={<Navigate to="/admin/mba/groups" replace />} />
            <Route
                path={ADMIN_ROUTES.MBA.path}
                element={
                    <ProtectedRoute accessPolicy="hasAdminAccess">
                        <AdminMba />
                    </ProtectedRoute>
                }
            />

            {/* Admin global metadata route */}
            <Route
                path={ADMIN_ROUTES.GLOBAL_METADATA.path}
                element={
                    <ProtectedRoute accessPolicy="hasAdminAccess">
                        <AdminGlobalMetadata />
                    </ProtectedRoute>
                }
            />
        </Route>
    );
}
